import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { ThemeProvider } from './context/ThemeContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { Toaster } from './components/ui/sonner';
import { ChuyChat } from './components/ChuyChat';
import DemoBanner from './components/DemoBanner';

// Pages
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import TouristDashboard from './pages/TouristDashboard';
import ProviderDashboard from './pages/ProviderDashboard';
import PartnerDashboard from './pages/PartnerDashboard';
import AdminDashboard from './pages/AdminDashboard';
import CreateRequestPage from './pages/CreateRequestPage';
import RequestDetailPage from './pages/RequestDetailPage';
import BookingDetailPage from './pages/BookingDetailPage';
import PaymentResultPage from './pages/PaymentResultPage';
import ProfilePage from './pages/ProfilePage';
import ProviderProfilePage from './pages/ProviderProfilePage';
import ReceiptsPage from './pages/ReceiptsPage';
import ProviderWithdrawalsPage from './pages/ProviderWithdrawalsPage';
import PartnerWithdrawalsPage from './pages/PartnerWithdrawalsPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import ProviderTermsPage from './pages/ProviderTermsPage';
import CityDetailPage from './pages/CityDetailPage';
import CatalogPage from './pages/CatalogPage';
import ProductDetailPage from './pages/ProductDetailPage';
import ChuyHelpPage from './pages/ChuyHelpPage';
import ChuyChatPage from './pages/ChuyChatPage';
import TranslatorPage from './pages/TranslatorPage';
import BookingsPage from './pages/BookingsPage';
import ProviderCatalogPage from './pages/ProviderCatalogPage';
import ProviderRequestsPage from './pages/ProviderRequestsPage';

import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-caribbean"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Dashboard Router - routes to correct dashboard based on role
const DashboardRouter = () => {
  const { user } = useAuth();
  
  if (!user) return <Navigate to="/auth" replace />;
  
  switch (user.role) {
    case 'admin':
      return <AdminDashboard />;
    case 'provider':
      return <ProviderDashboard />;
    case 'partner':
      return <PartnerDashboard />;
    default:
      return <TouristDashboard />;
  }
};

function AppContent() {
  return (
    <>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/auth" element={<AuthPage />} />
        
        {/* Provider Profile (public view) */}
        <Route path="/provider/:providerId" element={<ProviderProfilePage />} />
        
        {/* Legal Pages (public) */}
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/provider-terms" element={<ProviderTermsPage />} />
        
        {/* Protected Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardRouter />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/dashboard/create-request"
          element={
            <ProtectedRoute allowedRoles={['tourist']}>
              <CreateRequestPage />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/dashboard/requests/:requestId"
          element={
            <ProtectedRoute>
              <RequestDetailPage />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/dashboard/bookings/:bookingId"
          element={
            <ProtectedRoute>
              <BookingDetailPage />
            </ProtectedRoute>
          }
        />
        
        {/* Profile */}
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        
        {/* Receipts */}
        <Route
          path="/receipts"
          element={
            <ProtectedRoute>
              <ReceiptsPage />
            </ProtectedRoute>
          }
        />
        
        {/* Provider Withdrawals */}
        <Route
          path="/withdrawals"
          element={
            <ProtectedRoute allowedRoles={['provider']}>
              <ProviderWithdrawalsPage />
            </ProtectedRoute>
          }
        />
        
        {/* Partner Withdrawals */}
        <Route
          path="/partner/withdrawals"
          element={
            <ProtectedRoute allowedRoles={['partner']}>
              <PartnerWithdrawalsPage />
            </ProtectedRoute>
          }
        />
        
        {/* Payment Result Routes */}
        <Route
          path="/payment/success"
          element={
            <ProtectedRoute>
              <PaymentResultPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payment/cancel"
          element={
            <ProtectedRoute>
              <PaymentResultPage />
            </ProtectedRoute>
          }
        />
        
        {/* City Detail Page (public) */}
        <Route path="/city/:cityId" element={<CityDetailPage />} />

        {/* Product Catalog (public browse) */}
        <Route path="/catalog" element={<CatalogPage />} />
        <Route path="/product/:productId" element={<ProductDetailPage />} />

        {/* Chuy Help (public) */}
        <Route path="/chuy-help" element={<ChuyHelpPage />} />
        <Route path="/chuy-chat" element={<ChuyChatPage />} />
        <Route path="/translator" element={<TranslatorPage />} />

        {/* My Bookings (protected) */}
        <Route
          path="/bookings"
          element={
            <ProtectedRoute>
              <BookingsPage />
            </ProtectedRoute>
          }
        />

        {/* Provider sub-pages */}
        <Route
          path="/provider/catalog"
          element={
            <ProtectedRoute allowedRoles={['provider']}>
              <ProviderCatalogPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/provider/requests"
          element={
            <ProtectedRoute allowedRoles={['provider']}>
              <ProviderRequestsPage />
            </ProtectedRoute>
          }
        />

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      
      {/* Chuy Chat Widget */}
      <ChuyChat />
      
      {/* DEMO banner (preview only — hidden on bidtrip.live) */}
      <DemoBanner />
      
      {/* Toast Notifications */}
      <Toaster position="top-center" richColors />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <LanguageProvider>
          <CurrencyProvider>
            <AuthProvider>
              <AppContent />
            </AuthProvider>
          </CurrencyProvider>
        </LanguageProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
